// @flow

/**
 * Collection is provided three things: items, a renderItem method, and layout
 * information for those items. Based on these parts, Collection renders items
 * and places them on the screen at the specified locations. If an item is
 * variable sized and needs measuring, Collection will render it off-screen,
 * measure it, and notify its parent via the onRendered method.
 */

import * as React from 'react';
import isEqual from 'lodash/isEqual';
import Layout, {
  type Size,
  type ItemConstraints,
} from 'app/common/react/components/Masonry9/Layout';

export type renderItemProps<ItemType> = {|
  item: ItemType,
  index: number,
  isMeasuring: boolean,
  constraints?: ItemConstraints,
|};

// $FlowFixMe: new errors found from flow 0.96 upgrade
type Props<ItemType: {}> = {|
  renderItem: (props: renderItemProps<ItemType>) => React.Node,
  onRenderedItems?: (itemSizes: WeakMap<ItemType, Size>) => void,
  layout: Layout<ItemType>,
  customSize?: Size,
  itemsToMeasureLimit?: number,
|};

// $FlowFixMe: new errors found from flow 0.96 upgrade
export default class Collection<ItemType: {}> extends React.Component<Props<ItemType>> {
  componentDidMount() {
    this.componentDidRender();
  }

  shouldComponentUpdate(nextProps: Props<ItemType>) {
    const { customSize: newCustomSize, layout: newLayout } = nextProps;
    const { customSize, layout } = this.props;

    // Compare custom sizes.
    if (!isEqual(customSize, newCustomSize)) {
      return true;
    }

    // Compare layout item counts.
    if (newLayout.items.length !== layout.items.length) {
      return true;
    }

    // If any items differ, re-render.
    if (newLayout.items.some((newItem, i) => newItem !== layout.items[i])) {
      return true;
    }

    // Grab layout info for all items (in old and new layouts).
    const newLayoutInfo = newLayout.items.map(newLayout.getItemLayoutInfo);
    const layoutInfo = layout.items.map(layout.getItemLayoutInfo);

    // Check if all layout info pass reference checks. This'll happen when it's
    // just the virtualizer reducing layouts instead of new layouts being built.
    const itemLayoutInfoReferenceDiffers = newLayoutInfo.some(
      (newLayoutInfo, i) => newLayoutInfo !== layoutInfo[i],
    );

    // If we failed shallow comparison of layout info, we can still check deep.
    if (itemLayoutInfoReferenceDiffers) {
      if (newLayoutInfo.some((newLayoutInfo, i) => !isEqual(newLayoutInfo, layoutInfo[i]))) {
        return true;
      }
    }

    // No need to update.
    return false;
  }

  componentDidUpdate() {
    this.componentDidRender();
  }

  itemSizeCache: WeakMap<ItemType, Size> = new WeakMap();

  componentDidRender() {
    const { onRenderedItems } = this.props;

    // Now that we've rendered items, call back to the parent's onRenderedItems
    // with the rendered nodes' sizes. It is expected that the parent will then
    // merge those sizes back in with the layout info for each corresponding
    // item (which may then trigger another render of Collection).
    if (onRenderedItems) {
      onRenderedItems(this.itemSizeCache);
    }

    // Reset the size cache.
    this.itemSizeCache = new WeakMap();
  }

  measureItem(item: ItemType, ele: HTMLDivElement | null) {
    if (ele) {
      this.itemSizeCache.set(item, {
        width: ele.clientWidth || 0,
        height: ele.clientHeight || 0,
      });
    }
  }

  render() {
    const { renderItem, layout, customSize, itemsToMeasureLimit } = this.props;

    const measureNodes: Array<React.Node> = [];
    const displayNodes: Array<React.Node> = [];

    layout.items.forEach((item, index) => {
      const itemLayoutInfo = layout.getItemLayoutInfo(item);

      // Skip items with no layout info.
      if (!itemLayoutInfo) {
        return;
      }

      const itemIndex =
        typeof itemLayoutInfo.customIndex === 'number' ? itemLayoutInfo.customIndex : index;
      const key = `item-${itemIndex}`;

      // Measure items that don't have sizes/positions.
      if (!itemLayoutInfo.size) {
        if (measureNodes.length < (itemsToMeasureLimit || Infinity)) {
          const node = renderItem({
            item,
            index: itemIndex,
            isMeasuring: true,
            constraints: itemLayoutInfo.constraints,
          });

          measureNodes.push(
            <div
              className="Collection-Item Collection-Item-Measuring"
              style={{
                pointerEvents: 'none',
                position: 'absolute',
                visibility: 'hidden',
                top: -9999,
                left: -9999,
                ...itemLayoutInfo.constraints,
              }}
              key={key}
              ref={(ele: HTMLDivElement | null) => this.measureItem(item, ele)}
            >
              {node}
            </div>,
          );
        }
      }

      // Lay out items that do have sizes/positions.
      if (itemLayoutInfo.position) {
        const node = renderItem({
          item,
          index: itemIndex,
          isMeasuring: false,
          constraints: itemLayoutInfo.constraints,
        });

        if (!itemLayoutInfo.position) {
          return;
        }

        displayNodes.push(
          <div
            className="Collection-Item"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              transform: `translate(${itemLayoutInfo.position.left}px, ${
                itemLayoutInfo.position.top
              }px)`,
              ...itemLayoutInfo.size,
            }}
            key={key}
          >
            {node}
          </div>,
        );
      }
    });

    const size = customSize || layout.size;

    return (
      <div style={{ position: 'relative', ...size }} className="Collection">
        {measureNodes}
        {displayNodes}
      </div>
    );
  }
}
